import Icon from '@/components/ui/AppIcon';

interface Benefit {
  id: number;
  text: string;
}

const benefits: Benefit[] = [
  { id: 1, text: "Zero upfront costs or subscription fees" },
  { id: 2, text: "Keep 70% of all revenue you generate" },
  { id: 3, text: "Access to 10M+ active listeners" },
  { id: 4, text: "Professional creator tools and analytics" },
  { id: 5, text: "Dedicated creator support team" },
  { id: 6, text: "Free marketing and promotional support" }
];

interface FAQ {
  id: number;
  question: string;
  answer: string;
}

const faqs: FAQ[] = [
  {
    id: 1,
    question: "How long does the approval process take?",
    answer: "Most applications are reviewed within 48 hours. You'll receive an email notification once your application is approved."
  },
  {
    id: 2,
    question: "Do I need professional recording equipment?",
    answer: "While professional equipment helps, it's not mandatory. A good smartphone with a quiet recording environment is sufficient to get started."
  },
  {
    id: 3,
    question: "Can I create content in regional languages?",
    answer: "Absolutely! We support 15+ Indian languages and actively encourage regional content creation."
  },
  {
    id: 4,
    question: "When do I receive my earnings?",
    answer: "Earnings are calculated monthly and paid directly to your bank account within 7 business days of month-end."
  }
];

export default function CTASection() {
  return (
    <section className="py-16 bg-gradient-to-br from-primary to-primary-dark text-primary-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Icon name="RocketLaunchIcon" size={48} className="mx-auto mb-4" />
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
            Your Story Starts Here
          </h2>
          <p className="text-lg opacity-90 max-w-2xl mx-auto">
            Join India's fastest-growing audio content platform and turn your passion into a sustainable career
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-primary-foreground/10 backdrop-blur-sm rounded-2xl p-8">
            <h3 className="text-2xl font-heading font-bold mb-6">
              Why Creators Choose StoryNest-FM
            </h3>
            <div className="space-y-4">
              {benefits.map((benefit) => (
                <div key={benefit.id} className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="CheckIcon" size={16} className="text-success-foreground" />
                  </div>
                  <span className="leading-relaxed">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-primary-foreground/10 backdrop-blur-sm rounded-2xl p-8">
            <h3 className="text-2xl font-heading font-bold mb-6">
              Frequently Asked Questions
            </h3>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.id} className="border-b border-primary-foreground/20 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-start space-x-2 mb-2">
                    <Icon name="QuestionMarkCircleIcon" size={20} className="flex-shrink-0 mt-0.5" />
                    <h4 className="font-bold">{faq.question}</h4>
                  </div>
                  <p className="text-sm opacity-90 leading-relaxed ml-7">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4 mb-6">
            <button className="flex items-center justify-center space-x-2 px-8 py-4 bg-primary-foreground text-primary rounded-lg font-cta font-bold transition-all duration-300 hover:bg-opacity-90 hover:shadow-warm-lg hover:scale-105">
              <Icon name="PencilSquareIcon" size={20} />
              <span>Apply as Creator</span>
            </button>

            <button className="flex items-center justify-center space-x-2 px-8 py-4 bg-transparent border-2 border-primary-foreground rounded-lg font-cta font-bold transition-all duration-300 hover:bg-primary-foreground/10">
              <Icon name="ArrowDownTrayIcon" size={20} />
              <span>Download App</span>
            </button>
          </div>

          <p className="text-sm opacity-75 mb-2">
            Already a creator? <span className="font-bold underline cursor-pointer hover:opacity-100">Sign in to your dashboard</span>
          </p>
          <p className="text-xs opacity-60">
            By applying, you agree to our Creator Terms of Service and Content Guidelines
          </p>
        </div>
      </div>
    </section>
  );
}